import { useState, useEffect } from "react";

import useSupabase from "../../../../hooks/useSupabase";
import { useTournamentAuth } from "../../../../utils/authorization";
import { useAuth } from "../../../../context/AuthContext";
import { supabase } from "../../../../supabaseClient";
import { addCurrency, roundCurrency } from "../../../../utils/currencyUtils";
import { Card, AlertBox } from "../../../UI/DesignStandards";
import {
  sanitizeOperationError,
  logErrorForDebugging,
} from "../../../../utility/errorSanitization";

// Import the new focused components
import PrizeFundHeader from "./PrizeFundHeader";
import PrizeFundAddForm from "./PrizeFundAddForm";
import PrizeFundList from "./PrizeFundList";
import PrizeFundEditActions from "./PrizeFundEditActions";
import { useTournamentId } from "../../../../context/TournamentRouteContext";

const AdditionalPrizeFund = () => {
  const tournamentId = useTournamentId();
  const [prizeFunds, setPrizeFunds] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [newPrizeFund, setNewPrizeFund] = useState({
    description: "",
    amount: "",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [isPayoutFinalized, setIsPayoutFinalized] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [showAddForm, setShowAddForm] = useState(false);

  // TODO: Remove this feature flag after RLS testing is complete

  const { canManage } = useTournamentAuth(tournamentId);
  const { isLoading, error, fetchData, upsertData, insertData, deleteData } =
    useSupabase();
  const { user } = useAuth();

  const fetchPrizeFunds = async (isRealTimeUpdate = false) => {
    try {
      if (isRealTimeUpdate) {
        setIsRefreshing(true);
      }

      const response = await fetchData("tournamentAddedPrizeFund", {
        tournamentID: tournamentId,
      });

      const prizeFundsArray = Array.isArray(response) ? response : [];
      setPrizeFunds(prizeFundsArray);
    } catch (err) {
      setPrizeFunds([]);
    } finally {
      if (isRealTimeUpdate) {
        setIsRefreshing(false);
      }
    }
  };

  const fetchPayoutStatus = async () => {
    try {
      const { data: verificationStates, error } = await supabase
        .from("verificationStates")
        .select("*")
        .eq("tournamentId", tournamentId)
        .eq("entityType", "payouts")
        .is("entityId", null);

      if (error) throw error;

      // Handle case where no verification states exist yet
      const payoutVerification = verificationStates?.[0];
      setIsPayoutFinalized(payoutVerification?.isVerified || false);
    } catch (err) {
      // Error handling is silent for payout status, default to false
      setIsPayoutFinalized(false);
    }
  };

  useEffect(() => {
    if (tournamentId) {
      fetchPrizeFunds();
      fetchPayoutStatus();
    }

    // Set up real-time subscription for prize fund changes
    const prizeFundChannel = supabase
      .channel("prize_fund_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "tournamentAddedPrizeFund",
        },
        (payload) => {
          if (payload.eventType === "DELETE") {
            fetchPrizeFunds(true);
          } else {
            const prizeFund = payload.new;
            if (prizeFund && prizeFund.tournamentID === tournamentId) {
              fetchPrizeFunds(true);
            }
          }
        }
      )
      .subscribe();

    // Set up real-time subscription for verification changes (payout locking)
    const verificationChannel = supabase
      .channel("verification_changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "verificationStates",
        },
        (payload) => {
          if (
            payload.new &&
            payload.new.tournamentId === tournamentId &&
            payload.new.entityType === "payouts"
          ) {
            setIsPayoutFinalized(payload.new.isVerified || false);
          }
        }
      )
      .subscribe();

    return () => {
      if (prizeFundChannel) {
        prizeFundChannel.unsubscribe();
      }
      if (verificationChannel) {
        verificationChannel.unsubscribe();
      }
    };
  }, [tournamentId]);

  const toggleEditMode = () => {
    setEditMode((prev) => !prev);
    setValidationErrors({});
  };

  const handleSave = async () => {
    setValidationErrors({});
    setErrorMessage("");

    const currentPrizeFunds = Array.isArray(prizeFunds) ? prizeFunds : [];

    if (currentPrizeFunds.length === 0) {
      return;
    }

    // Validate all fields
    const errors = {};
    currentPrizeFunds.forEach((fund) => {
      if (!fund.description || fund.description.trim() === "") {
        errors[fund.id] = "Description is required";
      }
      if (!fund.amount || parseFloat(fund.amount) <= 0) {
        errors[fund.id] = "Amount must be a positive number";
      }
    });

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      setIsSubmitting(true);

      const result = await upsertData(
        "tournamentAddedPrizeFund",
        currentPrizeFunds,
        {
          id: currentPrizeFunds.map((fund) => fund.id),
        }
      );

      if (result.error) {
        const sanitizedMessage = sanitizeOperationError.save(
          result.error,
          "prize funds",
          isPayoutFinalized
        );
        logErrorForDebugging(
          result.error,
          "save prize funds",
          sanitizedMessage
        );
        setErrorMessage(sanitizedMessage);
        fetchPrizeFunds();
        return;
      }

      setEditMode(false);
      fetchPrizeFunds(true);
    } catch (err) {
      const sanitizedMessage = sanitizeOperationError.save(
        err,
        "prize funds",
        isPayoutFinalized
      );
      logErrorForDebugging(err, "save prize funds", sanitizedMessage);
      setErrorMessage(sanitizedMessage);
      fetchPrizeFunds();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setDeletingIds((prev) => new Set(prev).add(id));
      setErrorMessage("");

      const currentPrizeFunds = Array.isArray(prizeFunds) ? prizeFunds : [];

      // Optimistically remove from UI
      const optimisticPrizeFunds = currentPrizeFunds.filter(
        (fund) => fund.id !== id
      );
      setPrizeFunds(optimisticPrizeFunds);

      const result = await deleteData("tournamentAddedPrizeFund", { id: id });

      if (result.error) {
        const sanitizedMessage = sanitizeOperationError.delete(
          result.error,
          "prize fund",
          isPayoutFinalized
        );
        logErrorForDebugging(
          result.error,
          "delete prize fund",
          sanitizedMessage
        );
        setErrorMessage(sanitizedMessage);
        fetchPrizeFunds();
        return;
      }

      fetchPrizeFunds(true);
    } catch (err) {
      const sanitizedMessage = sanitizeOperationError.delete(
        err,
        "prize fund",
        isPayoutFinalized
      );
      logErrorForDebugging(err, "delete prize fund", sanitizedMessage);
      setErrorMessage(sanitizedMessage);
      fetchPrizeFunds();
    } finally {
      setDeletingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const handleAddPrizeFund = async () => {
    if (!newPrizeFund.description || newPrizeFund.description.trim() === "") {
      setValidationErrors({ new: "Description is required" });
      return;
    }
    if (!newPrizeFund.amount || parseFloat(newPrizeFund.amount) <= 0) {
      setValidationErrors({ new: "Amount must be a positive number" });
      return;
    }

    try {
      setIsSubmitting(true);
      setValidationErrors({});
      setErrorMessage("");

      const newFund = {
        description: newPrizeFund.description.trim(),
        amount: parseFloat(newPrizeFund.amount),
        tournamentID: tournamentId,
      };

      const currentPrizeFunds = Array.isArray(prizeFunds) ? prizeFunds : [];

      // Optimistically add to UI
      const optimisticPrizeFunds = [
        ...currentPrizeFunds,
        { ...newFund, id: `temp-${Date.now()}` },
      ];

      setPrizeFunds(optimisticPrizeFunds);
      setNewPrizeFund({ description: "", amount: "" });

      const result = await insertData("tournamentAddedPrizeFund", [newFund]);

      if (result.error) {
        const sanitizedMessage = sanitizeOperationError.create(
          result.error,
          "prize fund",
          isPayoutFinalized
        );
        logErrorForDebugging(
          result.error,
          "create prize fund",
          sanitizedMessage
        );
        setErrorMessage(sanitizedMessage);
        fetchPrizeFunds();
        return;
      }

      fetchPrizeFunds(true);
    } catch (err) {
      const sanitizedMessage = sanitizeOperationError.create(
        err,
        "prize fund",
        isPayoutFinalized
      );
      logErrorForDebugging(err, "create prize fund", sanitizedMessage);
      setErrorMessage(sanitizedMessage);
      fetchPrizeFunds();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDescriptionChange = (id, value) => {
    const currentPrizeFunds = Array.isArray(prizeFunds) ? prizeFunds : [];
    const newData = currentPrizeFunds.map((fund) =>
      fund.id === id ? { ...fund, description: value } : fund
    );
    setPrizeFunds(newData);
  };

  const handleAmountChange = (id, value) => {
    const currentPrizeFunds = Array.isArray(prizeFunds) ? prizeFunds : [];
    const newData = currentPrizeFunds.map((fund) =>
      fund.id === id ? { ...fund, amount: value } : fund
    );
    setPrizeFunds(newData);
  };

  const totalPrizeFund = (Array.isArray(prizeFunds) ? prizeFunds : []).reduce(
    (sum, prizeFund) => addCurrency(sum, parseFloat(prizeFund.amount) || 0),
    0
  );

  const handleAddPrizeFundClick = () => setShowAddForm(true);
  const handleEditPrizeFundsClick = () => toggleEditMode();
  const handleCancelEdit = () => setEditMode(false);
  const handleCancelAdd = () => {
    setShowAddForm(false);
    setNewPrizeFund({ description: "", amount: "" });
    setValidationErrors({});
  };

  return (
    <Card>
      <PrizeFundHeader
        totalPrizeFund={totalPrizeFund}
        isRefreshing={isRefreshing}
        isPayoutFinalized={isPayoutFinalized}
        canManage={canManage}
        showAddForm={showAddForm}
        prizeFunds={prizeFunds}
        onAddPrizeFund={handleAddPrizeFundClick}
        onEditPrizeFunds={handleEditPrizeFundsClick}
      />

      {errorMessage && (
        <div className="mb-4">
          <AlertBox
            type="error"
            title="There was a problem"
            message={errorMessage}
            onClose={() => setErrorMessage("")}
          />
        </div>
      )}

      <PrizeFundAddForm
        showAddForm={showAddForm}
        newPrizeFund={newPrizeFund}
        validationErrors={validationErrors}
        isSubmitting={isSubmitting}
        onAddPrizeFund={handleAddPrizeFund}
        onCancel={handleCancelAdd}
        onDescriptionChange={(value) =>
          setNewPrizeFund((prev) => ({ ...prev, description: value }))
        }
        onAmountChange={(value) =>
          setNewPrizeFund((prev) => ({ ...prev, amount: value }))
        }
      />

      <PrizeFundList
        isLoading={isLoading}
        prizeFunds={prizeFunds}
        isRefreshing={isRefreshing}
        editMode={editMode}
        validationErrors={validationErrors}
        onDescriptionChange={handleDescriptionChange}
        onAmountChange={handleAmountChange}
        onDelete={handleDelete}
        canManage={canManage}
        user={user}
        deletingIds={deletingIds}
        isPayoutFinalized={isPayoutFinalized}
      />

      <PrizeFundEditActions
        editMode={editMode}
        user={user}
        canManage={canManage}
        isSubmitting={isSubmitting}
        isPayoutFinalized={isPayoutFinalized}
        onCancel={handleCancelEdit}
        onSave={handleSave}
      />
    </Card>
  );
};

export default AdditionalPrizeFund;
